import { useAui } from "@assistant-ui/react";
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type {
  FilterHistoryEntry,
  RerunReport,
} from "./customInsightsChatUtils";
import type { FilterState } from "./CustomInsightsFiltersProvider";
import { useCustomInsightsFiltersContext } from "./CustomInsightsFiltersProvider";

export type { FilterHistoryEntry };

type CustomInsightsChatContextValue = {
  messageIdsWithCharts: ReadonlySet<string>;
  registerChartMessage: (messageId: string) => void;
  messageIdToSavedReport: Record<string, string>;
  markMessageSaved: (messageId: string, reportId: string) => void;
  filterHistory: readonly FilterHistoryEntry[];
  messageIdToFilterTimestamp: Readonly<Record<string, number>>;
  addFilterSnapshot: (state: FilterState) => void;
  rerunReport: RerunReport | null;
  setRerunReport: (report: RerunReport | null) => void;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  hasPreviewInterviews: boolean;
  isPreviewLoading: boolean;
  setPreviewAvailability: (availability: {
    hasPreviewInterviews: boolean;
    isPreviewLoading: boolean;
  }) => void;
  newExploration: () => void;
};

const CustomInsightsChatContext =
  createContext<CustomInsightsChatContextValue | null>(null);

export function CustomInsightsChatProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const aui = useAui();

  const [filtersOpen, setFiltersOpen] = useState(true);
  const { resetFilters } = useCustomInsightsFiltersContext();

  const [rerunReport, setRerunReport] = useState<RerunReport | null>(null);
  const [previewAvailability, setPreviewAvailability] = useState({
    hasPreviewInterviews: false,
    isPreviewLoading: true,
  });

  const [messageIdsWithCharts, setMessageIdsWithCharts] = useState<Set<string>>(
    () => new Set()
  );

  const [filterHistory, setFilterHistory] = useState<FilterHistoryEntry[]>([]);
  const [latestFilterTimestamp, setLatestFilterTimestamp] = useState(0);

  const addFilterSnapshot = useCallback((state: FilterState) => {
    const ts = Date.now();
    setLatestFilterTimestamp(ts);
    setFilterHistory((prev) => [...prev, { timestamp: ts, state }]);
  }, []);

  const [messageIdToFilterTimestamp, setMessageIdToFilterTimestamp] = useState<
    Record<string, number>
  >({});

  const registerChartMessage = useCallback(
    (messageId: string) => {
      setMessageIdsWithCharts((prev) => {
        if (prev.has(messageId)) return prev;
        const next = new Set(prev);
        next.add(messageId);
        return next;
      });
      if (latestFilterTimestamp > 0) {
        setMessageIdToFilterTimestamp((prev) =>
          prev[messageId]
            ? prev
            : { ...prev, [messageId]: latestFilterTimestamp }
        );
      }
    },
    [latestFilterTimestamp]
  );

  const [messageIdToSavedReport, setSavedMessageReports] = useState<
    Record<string, string>
  >({});
  const markMessageSaved = useCallback(
    (messageId: string, reportId: string) => {
      setSavedMessageReports((prev) => ({ ...prev, [messageId]: reportId }));
    },
    []
  );

  const newExploration = useCallback(() => {
    try {
      aui.thread().cancelRun();
    } catch {
      // Thread may not have an active run; safe to ignore.
    }
    setFiltersOpen(true);
    setFilterHistory([]);
    setMessageIdToFilterTimestamp({});
    setMessageIdsWithCharts(new Set());
    setSavedMessageReports({});
    setLatestFilterTimestamp(0);
    setRerunReport(null);
    setPreviewAvailability({
      hasPreviewInterviews: false,
      isPreviewLoading: true,
    });
    resetFilters();
    aui.threads().switchToNewThread();
  }, [aui, resetFilters]);

  const value = useMemo<CustomInsightsChatContextValue>(
    () => ({
      messageIdsWithCharts,
      registerChartMessage,
      messageIdToSavedReport,
      markMessageSaved,
      filterHistory,
      messageIdToFilterTimestamp,
      addFilterSnapshot,
      rerunReport,
      setRerunReport,
      filtersOpen,
      setFiltersOpen,
      hasPreviewInterviews: previewAvailability.hasPreviewInterviews,
      isPreviewLoading: previewAvailability.isPreviewLoading,
      setPreviewAvailability,
      newExploration,
    }),
    [
      messageIdsWithCharts,
      registerChartMessage,
      messageIdToSavedReport,
      markMessageSaved,
      filterHistory,
      messageIdToFilterTimestamp,
      addFilterSnapshot,
      rerunReport,
      filtersOpen,
      setFiltersOpen,
      previewAvailability.hasPreviewInterviews,
      previewAvailability.isPreviewLoading,
      newExploration,
    ]
  );
  return (
    <CustomInsightsChatContext.Provider value={value}>
      {children}
    </CustomInsightsChatContext.Provider>
  );
}

export function useCustomInsightsChat(): CustomInsightsChatContextValue {
  const ctx = useContext(CustomInsightsChatContext);
  if (!ctx)
    throw new Error(
      "useCustomInsightsChat must be used within CustomInsightsChatProvider"
    );
  return ctx;
}
